import Hls from 'hls.js'

const STATE = {
  IDLE: 'idle',
  LOADING: 'loading',
  READY: 'ready',
  PLAYING: 'playing',
  BUFFERING: 'buffering',
  RECOVERING: 'recovering',
  FAILED: 'failed',
  ENDED: 'ended',
}

const RECOVER_MAX_RETRY = 3

class HlsAudioManager {
  constructor(options = {}) {
    this._audio = null
    this._hls = null
    this._url = null
    this._state = STATE.IDLE
    this._retryCount = 0
    this._bufferingTimer = null
    this._lastCurrentTime = 0
    this._bufferingStartAt = 0

    this._onTimeUpdate = options.onTimeUpdate || (() => {})
    this._onStateChange = options.onStateChange || (() => {})
    this._onError = options.onError || (() => {})
    this._onEnded = options.onEnded || (() => {})
    this._shouldPlayAfterManifest = false

    this._createAudioElement()
  }

  _createAudioElement() {
    this._audio = document.createElement('audio')
    this._audio.preload = 'auto'
    this._audio.crossOrigin = 'anonymous'

    this._audio.addEventListener('timeupdate', () => {
      if (!this._audio) return
      this._onTimeUpdate(this._audio.currentTime)
    })

    this._audio.addEventListener('waiting', () => {
      if (!this._audio) return
      this._setState(STATE.BUFFERING)
      this._startBufferingTimer()
    })

    this._audio.addEventListener('playing', () => {
      if (!this._audio) return
      this._clearBufferingTimer()
      this._setState(STATE.PLAYING)
    })

    this._audio.addEventListener('pause', () => {
      if (!this._audio) return
      this._clearBufferingTimer()
    })

    this._audio.addEventListener('ended', () => {
      if (!this._audio) return
      this._setState(STATE.ENDED)
      this._onEnded()
    })

    this._audio.addEventListener('error', () => {
      if (!this._audio) return
      this._handleMediaError()
    })
  }

  _setState(newState) {
    if (this._state === newState) return
    this._state = newState
    this._onStateChange(newState)
  }

  _startBufferingTimer() {
    this._clearBufferingTimer()
    this._bufferingStartAt = Date.now()
    this._bufferingTimer = setInterval(() => {
      const elapsed = Date.now() - this._bufferingStartAt
      if (elapsed > 5000 && this._state === STATE.BUFFERING) {
        this._recover()
      }
    }, 1000)
  }

  _clearBufferingTimer() {
    if (this._bufferingTimer) {
      clearInterval(this._bufferingTimer)
      this._bufferingTimer = null
    }
  }

  _handleMediaError() {
    if (this._hls && Hls.isSupported()) {
      this._setState(STATE.RECOVERING)
      this._hls.recoverMediaError()
    } else {
      this._setState(STATE.FAILED)
      this._onError({ type: 'media_error', fatal: true })
    }
  }

  _recover() {
    if (this._retryCount >= RECOVER_MAX_RETRY) {
      this._setState(STATE.FAILED)
      this._onError({ type: 'recover_failed', fatal: true })
      return
    }
    this._retryCount++
    this._setState(STATE.RECOVERING)

    if (this._hls && Hls.isSupported()) {
      this._hls.recoverMediaError()
    } else {
      this.seek(this._lastCurrentTime)
      this.play()
    }
  }

  load(url, startTime = 0) {
    if (!url) return
    this._url = url
    this._startTime = startTime
    this._shouldPlayAfterManifest = false
    this.destroy()
    this._createAudioElement()
    this._setState(STATE.LOADING)

    if (Hls.isSupported()) {
      this._hls = new Hls({
        enableWorker: true,
        lowLatencyMode: false,
        backBufferLength: 90,
      })
      this._hls.loadSource(url)
      this._hls.attachMedia(this._audio)

      this._hls.on(Hls.Events.MANIFEST_PARSED, () => {
        this._setState(STATE.READY)
        this._retryCount = 0
        if (this._startTime > 0) {
          // 继续考试/seek 场景：先设置 currentTime，seek 完成后再 play
          const doPlay = () => {
            if (this._shouldPlayAfterManifest) {
              this._shouldPlayAfterManifest = false
              this.play()
            }
          }
          if (this._audio.readyState >= 2) {
            this._audio.currentTime = this._startTime
            doPlay()
          } else {
            const doSeek = () => {
              this._audio.currentTime = this._startTime
              this._audio.removeEventListener('loadedmetadata', doSeek)
              doPlay()
            }
            this._audio.addEventListener('loadedmetadata', doSeek)
          }
        } else {
          // 首次进入考试 startTime=0：保持原有逻辑，避免改动引入异常
          if (this._shouldPlayAfterManifest) {
            this._shouldPlayAfterManifest = false
            this.play()
          }
        }
      })

      this._hls.on(Hls.Events.ERROR, (event, data) => {
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              this._setState(STATE.BUFFERING)
              this._startBufferingTimer()
              break
            case Hls.ErrorTypes.MEDIA_ERROR:
              this._handleMediaError()
              break
            default:
              this._setState(STATE.FAILED)
              this._onError({ type: data.type, fatal: true, details: data.details })
              break
          }
        }
      })
    } else if (this._audio.canPlayType('application/vnd.apple.mpegurl')) {
      this._audio.src = url
      this._audio.addEventListener('loadedmetadata', () => {
        this._setState(STATE.READY)
        if (this._startTime > 0) {
          this.seek(this._startTime)
        }
      })
    } else {
      this._setState(STATE.FAILED)
      this._onError({ type: 'not_supported', fatal: true })
    }
  }

  play() {
    if (!this._audio) return
    // 如果 m3u8 还没解析完成，先标记，等 MANIFEST_PARSED 后再播放
    if (this._state === STATE.LOADING) {
      this._shouldPlayAfterManifest = true
      return
    }
    const promise = this._audio.play()
    if (promise && promise.catch) {
      promise.catch((err) => {
        console.warn('HLS play error:', err)
      })
    }
  }

  pause() {
    if (this._audio) {
      this._audio.pause()
    }
  }

  stop() {
    this.pause()
    if (this._audio) {
      this._audio.currentTime = 0
      this._audio.src = ''
    }
  }

  seek(time) {
    if (!this._audio) return
    this._lastCurrentTime = time
    if (this._audio.readyState >= 2) {
      this._audio.currentTime = time
    } else {
      const doSeek = () => {
        this._audio.currentTime = time
        this._audio.removeEventListener('loadedmetadata', doSeek)
      }
      this._audio.addEventListener('loadedmetadata', doSeek)
    }
  }

  resume() {
    this._retryCount = 0
    this.play()
  }

  get currentTime() {
    return this._audio ? this._audio.currentTime : 0
  }

  get duration() {
    return this._audio ? this._audio.duration : 0
  }

  get state() {
    return this._state
  }

  destroy() {
    this._clearBufferingTimer()
    if (this._hls) {
      this._hls.destroy()
      this._hls = null
    }
    if (this._audio) {
      this._audio.pause()
      this._audio.src = ''
      this._audio = null
    }
    this._state = STATE.IDLE
  }
}

export default HlsAudioManager
export { STATE }
